import type { ClientOnboardingConfiguration, DepartmentInput, LocationInput, ProfileInput } from '../../onboarding/model.js';

export class ClientOnboardingRequestError extends Error {
  constructor(message: string) { super(message); this.name = 'ClientOnboardingRequestError'; }
}

export class ClientOnboardingProvider {
  load(token: string, clientId: string): Promise<ClientOnboardingConfiguration> {
    return request(token, clientId, '', 'GET') as Promise<ClientOnboardingConfiguration>;
  }
  issueToken(token: string, clientId: string, rotate: boolean): Promise<{ rawToken: string; tokenLastRotatedAt: string }> {
    return request(token, clientId, '/token', 'POST', { rotate }) as Promise<{ rawToken: string; tokenLastRotatedAt: string }>;
  }
  setTokenActive(token: string, clientId: string, active: boolean): Promise<ClientOnboardingConfiguration> {
    return request(token, clientId, '/token', 'PUT', { active }) as Promise<ClientOnboardingConfiguration>;
  }
  saveLocation(token: string, clientId: string, input: LocationInput): Promise<ClientOnboardingConfiguration> {
    return request(token, clientId, '/locations', 'PUT', input) as Promise<ClientOnboardingConfiguration>;
  }
  saveDepartment(token: string, clientId: string, input: DepartmentInput): Promise<ClientOnboardingConfiguration> {
    return request(token, clientId, '/departments', 'PUT', input) as Promise<ClientOnboardingConfiguration>;
  }
  saveProfile(token: string, clientId: string, input: ProfileInput): Promise<ClientOnboardingConfiguration> {
    return request(token, clientId, '/profiles', 'PUT', input) as Promise<ClientOnboardingConfiguration>;
  }
}

async function request(token: string, clientId: string, suffix: string, method: 'GET' | 'POST' | 'PUT', body?: unknown): Promise<unknown> {
  if (!token) throw new ClientOnboardingRequestError('Authenticated session is unavailable');
  let response: Response;
  try {
    response = await fetch(`/api/clients/${encodeURIComponent(clientId)}/onboarding${suffix}`, {
      method, cache: 'no-store',
      headers: { Authorization: `Bearer ${token}`, ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch { throw new ClientOnboardingRequestError('Onboarding configuration service is unavailable'); }
  const payload = await response.json().catch(() => null) as unknown;
  if (!response.ok) {
    throw new ClientOnboardingRequestError(
      typeof payload === 'object' && payload !== null && 'error' in payload && typeof payload.error === 'string'
        ? payload.error : 'Onboarding configuration could not be saved',
    );
  }
  return payload;
}
